import { createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { ProListItem } from 'Douleutaras/types';

export const API_PROS_LIST_ENDPOINT = 'api/public/v3/pros_list';

type AttributeValue = string | number | Array<string | number>;
export interface AttributeFilter {
  attribute: string | number;
  value: AttributeValue;
}

interface FetchProsListLocationArgs {
  location: string;
  categoryForm?: string;
  questions?: {
    [key: number]: number;
  };
}

interface FetchProsListJobArgs {
  jobId: string;
}

export type FetchProsListArgs = FetchProsListLocationArgs | FetchProsListJobArgs;

const prosListAdapter = createEntityAdapter<ProListItem>();

interface ProsListExtraState {
  loading: boolean;
  error: boolean;
  lastQuery?: FetchProsListArgs;
  firstLoad: boolean;
}

const initialState = prosListAdapter.getInitialState<ProsListExtraState>({
  loading: false,
  error: false,
  lastQuery: undefined,
  firstLoad: false
});

export const fetchProsList = createAsyncThunk<Array<ProListItem>, FetchProsListLocationArgs>(
  'prosList/fetch',
  async ({ location, categoryForm, questions = {} }, api) => {
    const additionalGetParams: Record<string, string | number | Array<string | number>> = {
      location
    };

    if (categoryForm !== undefined) {
      additionalGetParams.category_form = categoryForm;
    }

    if (questions && Object.keys(questions).length > 0) {
        Object.keys(questions)
          .forEach(question => {
            additionalGetParams[question] = Array.isArray(questions[question])
              // Separate the answers by comma if multiple provided. Lists should have brackets.
              ? `[${questions[question].join(',')}]`
              : questions[question];
          });
    }

    const response = await makeRequest(API_PROS_LIST_ENDPOINT, {
      method: 'GET',
      additionalGetParams
    })(api);

    const ok = response.status === 200 || response.status === 201;
    if (!ok) {
      return [];
    }

    return response.data.data;
  }
);

export const fetchProsListByJob = createAsyncThunk<Array<ProListItem>, FetchProsListJobArgs>(
  'prosList/fetch/byJob',
  async ({ jobId }, api) => {
    const response = await makeRequest(API_PROS_LIST_ENDPOINT, {
      method: 'GET',
      additionalGetParams: {
        job_id: jobId
      }
    })(api);

    const ok = response.status === 200 || response.status === 201;
    if (!ok) {
      return [];
    }

    return response.data.data;
  }
);

export const prosListSlice = createSlice({
  name: 'prosList',
  initialState,
  reducers: {
    clearProsList: state => {
      prosListAdapter.removeAll(state);
      state.loading = false;
      state.error = false;
      state.lastQuery = undefined;
      state.firstLoad = false;
    },
    updateLoadingState: (state, action) => {
      state.loading = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProsList.pending, (state, action) => {
        state.loading = true;
        state.error = false;
        state.lastQuery = action.meta.arg;
      })
      .addCase(fetchProsList.fulfilled, (state, action) => {
        prosListAdapter.setAll(state, action.payload);
        state.loading = false;
        state.error = false;
        state.firstLoad = true;
      })
      .addCase(fetchProsList.rejected, state => {
        state.loading = false;
        state.error = true;
        state.firstLoad = true;
      })
      .addCase(fetchProsListByJob.pending, (state, action) => {
        state.loading = true;
        state.error = false;
        state.lastQuery = action.meta.arg;
      })
      .addCase(fetchProsListByJob.fulfilled, (state, action) => {
        prosListAdapter.setAll(state, action.payload);
        state.loading = false;
        state.error = false;
        state.firstLoad = true;
      })
      .addCase(fetchProsListByJob.rejected, state => {
        state.loading = false;
        state.error = true;
        state.firstLoad = true;
      });
  }
});

export const { clearProsList, updateLoadingState } = prosListSlice.actions;
export const prosListReducer = prosListSlice.reducer;

type ProsListState = ReturnType<typeof prosListReducer>;
export const prosListSelectors = prosListAdapter.getSelectors(
  (state: { prosList: ProsListState }) => state.prosList
);

export const selectActivePros = createSelector(
  [prosListSelectors.selectAll],
  pros => pros.filter(pro => !pro.attributes.hasNoCapacity)
);

export const selectUnavailablePros = createSelector(
  [prosListSelectors.selectAll],
  pros => pros.filter(pro => pro.attributes.hasNoCapacity)
);
